import React, { useMemo } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import {
    Chat as ChatIcon,
    Psychology as TrainingIcon,
    Memory as MemoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ConversationTab } from '../types/planQa';

interface ChatTabBarProps {
    activeTab: ConversationTab;
    onTabChange: (tabId: ConversationTab) => void;
    hasRoadmap: boolean;
    hasPlanQaMessages: boolean;
    hasPetMemory: boolean;
}

const ChatTabBar: React.FC<ChatTabBarProps> = ({
    activeTab,
    onTabChange,
    hasRoadmap,
    hasPlanQaMessages,
    hasPetMemory
}) => {
    const { t } = useTranslation();

    const tabs = useMemo(() => {
        const availableTabs: { id: ConversationTab; label: string; icon: React.ReactElement }[] = [
            { id: 'general', label: t('generalChat'), icon: <ChatIcon /> }
        ];

        // Only show Plan Q&A tab if there's a current roadmap or existing Plan Q&A messages
        if (hasRoadmap || hasPlanQaMessages) {
            availableTabs.push({
                id: 'planQa',
                label: t('planQA'),
                icon: <TrainingIcon />
            });
        }

        // Show memory tab if we have pet information
        if (hasPetMemory) {
            availableTabs.push({
                id: 'memory',
                label: t('memory') || 'Memory',
                icon: <MemoryIcon />
            });
        }

        return availableTabs;
    }, [hasRoadmap, hasPlanQaMessages, hasPetMemory, t]);

    if (tabs.length <= 1) return null;

    return (
        <Tabs
            value={activeTab}
            onChange={(_, newValue) => onTabChange(newValue)}
            variant="fullWidth"
            sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'white',
                minHeight: 48
            }}
        >
            {tabs.map(tab => (
                <Tab
                    key={tab.id}
                    value={tab.id}
                    label={tab.label}
                    icon={tab.icon}
                    iconPosition="start"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.9rem'
                    }}
                />
            ))}
        </Tabs>
    );
};

export default ChatTabBar;
